import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DealerLaunchStep, DealerLaunchStepProps } from './types';
import { Dealer } from '../../types/dealerTypes';

interface SubStep<T = undefined> {
  Component: FunctionComponent<DealerLaunchStepProps<T>>;
  isComplete: (dealer: Dealer) => boolean;
  stepProps: T;
  skipStep?: (dealer: Dealer) => boolean;
}

const DealerLaunchMultiStepFactory: FunctionComponent<DealerLaunchStepProps<{
  subSteps: SubStep[];
}>> = ({ step, ...props }) => {
  const history = useHistory();

  // rerender the component whenever there's a change to history
  const [, rerender] = useState(0);
  useEffect(
    () =>
      history.listen(() => {
        rerender((i) => i + 1);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { subSteps } = step.stepProps;

  // return the next substep index (after considering skipStep())
  const getNextMultistepIndex = (fromIndex: number) => {
    let toIndex = fromIndex;
    while (
      toIndex < subSteps.length &&
      !!subSteps[toIndex].skipStep?.(props.dealer)
    ) {
      toIndex += 1;
    }
    return toIndex;
  };

  // store the current step in the browser history
  const stepIndex = getNextMultistepIndex(
    (history.location.state as { stepIndex?: number })?.stepIndex || 0,
  );

  // in the event we skip all substep steps, we navigate to next launch step
  if (stepIndex === subSteps.length) {
    props?.skipStep?.();
    return <></>;
  }

  const subStep = subSteps[stepIndex];

  return (
    <subStep.Component
      key={stepIndex}
      {...props}
      step={{ ...step, ...subStep }}
      // replace nextStep and prevStep to handle navigating to other sub steps
      nextStep={async (dealerUpdate) => {
        if (stepIndex === subSteps.length - 1) {
          await props.nextStep(dealerUpdate);
        } else {
          if (dealerUpdate) {
            await props.saveDealer(dealerUpdate);
          }
          history.push(history.location.pathname, {
            stepIndex: getNextMultistepIndex(stepIndex + 1),
          });
        }
      }}
      prevStep={() => {
        if (stepIndex === 0) {
          props.prevStep();
        } else {
          history.goBack();
        }
      }}
    />
  );
};

/**
 * Creates a step composed of multiple sub steps
 */
export const createMultiStep = (
  multiStepProps: Omit<
    DealerLaunchStep,
    'Component' | 'stepProps' | 'isComplete'
  >,
  subSteps: SubStep<any>[],
): DealerLaunchStep<{
  subSteps: SubStep<any>[];
}> => ({
  ...multiStepProps,
  Component: DealerLaunchMultiStepFactory,
  isComplete: (dealer) =>
    subSteps.every((subStep) => subStep.isComplete(dealer)),
  stepProps: { subSteps },
});

/**
 * Creates a step for use in createMultiStep
 */
export function createSubStep<T>(
  step: T extends undefined ? Omit<SubStep, 'stepProps'> : SubStep<T>,
): SubStep<T> {
  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore typescript doesn't understand the step parameter type above, so its check needs to be disabled
    stepProps: undefined,
    skipStep: () => false,
    ...step,
  } as SubStep<T>;
}
